import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "searchInput",
    "unassociatedList",
    "associatedList",
    "applyAllButton",
    "removeAllButton",
    "associatedCount",
    "emptyUnassociated",
    "emptyAssociated",
    "notificationsToggle",
  ]

  static values = {
    createUrl: String,
    destroyUrl: String,
    userId: Number,
  }

  connect() {
    this.filter()
    this._syncEmptyStates()
    this.syncNotificationToggle()
  }

  filter() {
    const query = this.searchInputTarget.value.trim().toLowerCase()
    let visible = 0

    this.unassociatedListTarget
      .querySelectorAll('[data-role="unassociated-row"]')
      .forEach((row) => {
        const show = query === "" || row.dataset.name.includes(query)
        row.classList.toggle("d-none", !show)
        if (show) visible += 1
      })

    if (this.hasApplyAllButtonTarget) {
      this.applyAllButtonTarget.disabled = visible === 0
    }
    if (this.hasRemoveAllButtonTarget) {
      const associatedCount = this.associatedListTarget.querySelectorAll('[data-role="associated-row"]').length
      this.removeAllButtonTarget.disabled = associatedCount === 0
    }
  }

  associateOne(event) {
    const row = event.currentTarget.closest('[data-role="unassociated-row"]')
    if (!row) return
    this._createPairs([parseInt(row.dataset.affiliateId, 10)])
  }

  applyAll() {
    const ids = Array.from(
      this.unassociatedListTarget.querySelectorAll('[data-role="unassociated-row"]:not(.d-none)')
    ).map((row) => parseInt(row.dataset.affiliateId, 10))

    if (ids.length === 0) return
    this._createPairs(ids)
  }

  removeOne(event) {
    const row = event.currentTarget.closest('[data-role="associated-row"]')
    if (!row) return
    this._destroyPairs([parseInt(row.dataset.pairId, 10)])
  }

  removeAll() {
    const ids = Array.from(
      this.associatedListTarget.querySelectorAll('[data-role="associated-row"]')
    ).map((row) => parseInt(row.dataset.pairId, 10))

    if (ids.length === 0) return
    this._destroyPairs(ids)
  }

  async toggleAllNotifications(event) {
    const checked = event.target.checked
    const notificationRows = this._notificationRows()
    if (notificationRows.length === 0) return

    event.target.disabled = true

    try {
      await Promise.all(
        notificationRows.map(async (row) => {
          const checkbox = row.querySelector('[data-notifications-target="checkbox"]')
          if (!checkbox || checkbox.checked === checked) return

          const response = await fetch(row.dataset.notificationsUrlValue, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": this._csrf(),
            },
            body: JSON.stringify({ receives_notifications: checked }),
          })

          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          checkbox.checked = checked
        })
      )

      this.syncNotificationToggle()
    } catch (error) {
      window.alert(`Unable to update notifications: ${error.message}`)
      this.syncNotificationToggle()
    } finally {
      event.target.disabled = false
    }
  }

  syncNotificationToggle() {
    if (!this.hasNotificationsToggleTarget) return

    const checkboxes = this._notificationCheckboxes()
    this.notificationsToggleTarget.disabled = checkboxes.length === 0

    if (checkboxes.length === 0) {
      this.notificationsToggleTarget.checked = false
      this.notificationsToggleTarget.indeterminate = false
      return
    }

    const checkedCount = checkboxes.filter((checkbox) => checkbox.checked).length
    this.notificationsToggleTarget.checked = checkedCount === checkboxes.length
    this.notificationsToggleTarget.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length
  }
  async _createPairs(affiliateIds) {
    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this._csrf(),
        },
        body: JSON.stringify({
          user_id: this.userIdValue,
          affiliate_ids: affiliateIds,
        }),
      })

      const data = await response.json()
      if (!response.ok || !data.success) throw new Error(data.error || `HTTP ${response.status}`)

      data.created.forEach((pair) => this._moveToAssociated(pair))
      this.filter()
      this._syncEmptyStates()
    } catch (error) {
      window.alert(`Unable to associate affiliate(s): ${error.message}`)
    }
  }

  async _destroyPairs(pairIds) {
    try {
      const response = await fetch(this.destroyUrlValue, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this._csrf(),
        },
        body: JSON.stringify({ affiliate_user_pair_ids: pairIds }),
      })

      const data = await response.json()
      if (!response.ok || !data.success) throw new Error(data.error || `HTTP ${response.status}`)

      data.removed_ids.forEach((id) => this._moveToUnassociated(id))
      this.filter()
      this._syncEmptyStates()
    } catch (error) {
      window.alert(`Unable to remove affiliate association: ${error.message}`)
    }
  }

  _moveToAssociated(pair) {
    const row = this.unassociatedListTarget.querySelector(
      `[data-role="unassociated-row"][data-affiliate-id="${pair.affiliate_id}"]`
    )
    if (!row) return

    row.remove()
    this.associatedListTarget.insertAdjacentHTML("afterbegin", `
      <div class="list-group-item d-flex justify-content-between align-items-center gap-2"
           data-role="associated-row"
           data-name="${this._esc(pair.affiliate_name.toLowerCase())}"
           data-pair-id="${pair.id}"
           data-affiliate-id="${pair.affiliate_id}">
        <div class="form-check mb-0"
             data-controller="notifications"
             data-notifications-id-value="${pair.id}"
             data-notifications-url-value="/affiliate_user_pairs/${pair.id}"
             data-action="change->notifications#toggleState">
          <input type="checkbox"
                 id="receives_notifications_${pair.id}"
                 class="form-check-input mt-0"
                 data-notifications-target="checkbox"
                 aria-label="Notifications for ${this._esc(pair.affiliate_name)}"
                 ${pair.receives_notifications ? "checked" : ""}>
        </div>
        <div class="flex-grow-1">
          <a class="text-decoration-none" href="/affiliates/${pair.affiliate_id}">${this._esc(pair.affiliate_name)}</a>
          <div class="text-muted small mt-1">Notification preference for this affiliate</div>
        </div>
        <button type="button"
                class="btn btn-link text-danger p-0"
                title="Remove affiliate association"
                aria-label="Remove ${this._esc(pair.affiliate_name)}"
                data-action="click->user-affiliations#removeOne">
          <i class="bi bi-circle-fill"></i>
        </button>
      </div>
    `)
    this._updateAssociatedCount(1)
    this.syncNotificationToggle()
  }

  _moveToUnassociated(pairId) {
    const row = this.associatedListTarget.querySelector(
      `[data-role="associated-row"][data-pair-id="${pairId}"]`
    )
    if (!row) return

    const affiliateId = row.dataset.affiliateId
    const name = row.dataset.name
    const label = row.querySelector("a")?.textContent?.trim() || name
    row.remove()
    this.unassociatedListTarget.insertAdjacentHTML("beforeend", `
      <div class="list-group-item d-flex justify-content-between align-items-center gap-2"
           data-role="unassociated-row"
           data-name="${this._esc(name)}"
           data-affiliate-id="${affiliateId}">
        <a class="text-decoration-none flex-grow-1" href="/affiliates/${affiliateId}">${this._esc(label)}</a>
        <button type="button"
                class="btn btn-link text-success p-0"
                title="Associate affiliate"
                aria-label="Associate ${this._esc(label)}"
                data-action="click->user-affiliations#associateOne">
          <i class="bi bi-circle-fill"></i>
        </button>
      </div>
    `)
    this._updateAssociatedCount(-1)
    this.syncNotificationToggle()
  }

  _updateAssociatedCount(delta) {
    const current = parseInt(this.associatedCountTarget.textContent, 10) || 0
    this.associatedCountTarget.textContent = current + delta
  }

  _syncEmptyStates() {
    const unassociatedRows = this.unassociatedListTarget.querySelectorAll('[data-role="unassociated-row"]').length
    const associatedRows = this.associatedListTarget.querySelectorAll('[data-role="associated-row"]').length

    this.emptyUnassociatedTarget.classList.toggle("d-none", unassociatedRows > 0)
    this.emptyAssociatedTarget.classList.toggle("d-none", associatedRows > 0)
    if (this.hasRemoveAllButtonTarget) {
      this.removeAllButtonTarget.disabled = associatedRows === 0
    }
    this.syncNotificationToggle()
  }

  _notificationRows() {
    return Array.from(
      this.associatedListTarget.querySelectorAll('[data-controller~="notifications"]')
    )
  }

  _notificationCheckboxes() {
    return this._notificationRows()
      .map((row) => row.querySelector('[data-notifications-target="checkbox"]'))
      .filter(Boolean)
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _esc(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
